import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Plus, Mail, Copy, X, Crown, LogOut, Loader2, Check, AlertCircle,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { adsApi } from "../ads-creator/api";
import { StudioShell } from "./StudioShell";
import { useLanguage } from "../contexts/LanguageContext";

export default function StudioTeam() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adsApi.get("/team/me");
      if (!r.data.team) {
        setTeam(null);
        setLoading(false);
        return;
      }
      setTeam(r.data.team);
      setIsOwner(!!r.data.is_owner);
      const m = await adsApi.get("/team/members");
      setMembers(m.data.members || []);
      setPending(m.data.pending_invites || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error(t.studio_team_load_fail || "Couldn't load team");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <StudioShell activeKey="/studio/team">
      <Toaster position="top-right" theme="dark" />
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "40px 20px 40px" }}
        data-testid="studio-team-page">
        <h1 style={{
          color: "#fff", fontSize: "clamp(28px,4vw,42px)", fontWeight: 900,
          letterSpacing: "-0.02em", margin: "0 0 8px",
        }}>
          <Crown size={28} style={{ color: "#fde047", display: "inline", marginRight: 10 }} />
          {t.studio_team_title || "Business — team workspace"}
        </h1>
        <p style={{ color: "#a5a5c0", fontSize: 14, margin: "0 0 28px" }}>
          {t.studio_team_sub || "Invite colleagues to work together. Max. 10 members per team."}
        </p>

        {loading && (
          <div style={{ color: "#8b8ba7", padding: 40, textAlign: "center" }}>
            <Loader2 className="animate-spin" style={{ margin: "0 auto" }} size={24} />
          </div>
        )}

        {!loading && !team && <NoTeamView onCreate={refresh} t={t} />}

        {!loading && team && (
          <TeamView
            team={team}
            members={members}
            pending={pending}
            isOwner={isOwner}
            onChange={refresh}
            t={t}
          />
        )}
      </div>
    </StudioShell>
  );
}

function NoTeamView({ onCreate, t = {} }) {
  const [name, setName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function create() {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      await adsApi.post("/team/create", { name: name.trim() });
      toast.success(t.studio_team_created || "Team created");
      onCreate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_team_failed || "Failed");
    } finally { setBusy(false); }
  }

  async function join() {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      await adsApi.post("/team/join", { code: joinCode.trim() });
      toast.success(t.studio_team_joined || "Joined team");
      onCreate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_team_invite_invalid || "Invite invalid");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{
        padding: 20, background: "rgba(20,20,35,0.72)",
        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14,
      }}>
        <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
          {t.studio_team_create_title || "Create a new team"}
        </div>
        <div style={{ color: "#a5a5c0", fontSize: 13, marginBottom: 12 }}>
          {t.studio_team_create_sub || "You become the owner and can invite members."}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="team-create-name"
            type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.studio_team_name_placeholder || "Team name"}
            style={{
              flex: 1, padding: "10px 12px", background: "rgba(0,0,0,0.3)",
              color: "#fff", border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8, fontSize: 14,
            }}
          />
          <button
            data-testid="team-create-btn"
            onClick={create} disabled={busy || name.trim().length < 2}
            style={{
              padding: "10px 18px",
              background: "linear-gradient(135deg,#a855f7,#7c3aed)",
              border: 0, borderRadius: 8,
              color: "#fff", fontWeight: 800, cursor: "pointer",
              opacity: busy || name.trim().length < 2 ? 0.6 : 1,
            }}
          >
            <Plus size={16} style={{ verticalAlign: -2 }} /> {t.studio_team_create_btn || "Create"}
          </button>
        </div>
      </div>

      <div style={{
        padding: 20, background: "rgba(20,20,35,0.72)",
        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14,
      }}>
        <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
          {t.studio_team_join_title || "Join with an existing invite"}
        </div>
        <div style={{ color: "#a5a5c0", fontSize: 13, marginBottom: 12 }}>
          {t.studio_team_join_sub || "If someone sent you an invite code."}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="team-join-code"
            type="text" value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t.studio_team_invite_code_placeholder || "Invite code"}
            style={{
              flex: 1, padding: "10px 12px", background: "rgba(0,0,0,0.3)",
              color: "#fff", border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8, fontSize: 14, fontFamily: "monospace",
            }}
          />
          <button
            data-testid="team-join-btn"
            onClick={join} disabled={busy || !joinCode.trim()}
            style={{
              padding: "10px 18px", background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8, color: "#fff", fontWeight: 800, cursor: "pointer",
              opacity: busy || !joinCode.trim() ? 0.6 : 1,
            }}
          >
            {t.studio_team_join_btn || "Join"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamView({ team, members, pending, isOwner, onChange, t = {} }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  async function sendInvite() {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    try {
      const r = await adsApi.post("/team/invite", { email: inviteEmail.trim() });
      await navigator.clipboard.writeText(r.data.invite_url);
      toast.success(t.studio_team_invite_copied || "Invite link copied to clipboard");
      setInviteEmail("");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_team_failed || "Failed");
    } finally { setInviteBusy(false); }
  }

  async function leaveTeam() {
    if (!window.confirm(t.studio_team_confirm_leave || "Really leave the team?")) return;
    try {
      await adsApi.post("/team/leave", {});
      toast.success(t.studio_team_left || "Left team");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.studio_team_failed || "Failed");
    }
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* Team header */}
      <div style={{
        padding: 20, background: "linear-gradient(135deg,rgba(168,85,247,0.08),rgba(124,58,237,0.08))",
        border: "1px solid rgba(168,85,247,0.25)", borderRadius: 14,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 50, height: 50, borderRadius: 12,
          background: "linear-gradient(135deg,#a855f7,#7c3aed)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 22, fontWeight: 900,
        }}>{team.name[0]?.toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#fff", fontSize: 20, fontWeight: 900, letterSpacing: "-0.01em" }}
            data-testid="team-name">{team.name}</div>
          <div style={{ color: "#a5a5c0", fontSize: 13 }}>
            {members.length} {members.length === 1 ? (t.studio_team_member_one || "member") : (t.studio_team_members_many || "members")}
            {isOwner && ` ${t.studio_team_you_are_owner || "· you are owner"}`}
          </div>
        </div>
        {!isOwner && (
          <button onClick={leaveTeam} data-testid="team-leave-btn" style={{
            padding: "8px 14px", background: "rgba(248,113,113,0.12)",
            color: "#fca5a5", border: "1px solid rgba(248,113,113,0.3)",
            borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <LogOut size={14} /> {t.studio_team_leave_btn || "Leave"}
          </button>
        )}
      </div>

      {/* Invite bar — owners only */}
      {isOwner && (
        <div style={{
          padding: 18, background: "rgba(20,20,35,0.72)",
          border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14,
        }}>
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, marginBottom: 10,
            display: "flex", alignItems: "center", gap: 8 }}>
            <Mail size={16} /> {t.studio_team_invite_title || "Invite a new member"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              data-testid="team-invite-email"
              type="email" value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t.studio_team_invite_email_placeholder || "email@example.com"}
              onKeyDown={(e) => e.key === "Enter" && sendInvite()}
              style={{
                flex: 1, padding: "10px 12px", background: "rgba(0,0,0,0.3)",
                color: "#fff", border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8, fontSize: 14,
              }}
            />
            <button
              data-testid="team-invite-send"
              onClick={sendInvite} disabled={inviteBusy || !inviteEmail.trim()}
              style={{
                padding: "10px 18px",
                background: "linear-gradient(135deg,#a855f7,#7c3aed)",
                border: 0, borderRadius: 8,
                color: "#fff", fontWeight: 800, cursor: "pointer",
                opacity: inviteBusy || !inviteEmail.trim() ? 0.6 : 1,
              }}
            >
              {t.studio_team_send_invite || "Send invite"}
            </button>
          </div>
          <div style={{ color: "#8b8ba7", fontSize: 11, marginTop: 8,
            display: "flex", alignItems: "center", gap: 4 }}>
            <AlertCircle size={11} /> {t.studio_team_auto_copy_hint || "Invite link is copied to clipboard automatically."}
          </div>
        </div>
      )}

      {/* Members list */}
      <div style={{
        background: "rgba(20,20,35,0.72)",
        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden",
      }}>
        <div style={{
          padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,0.05)",
          color: "#8b8ba7", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}>
          {t.studio_team_members_header || "Members"} ({members.length})
        </div>
        {members.map((m) => (
          <div key={m.email} data-testid={`team-member-${m.email}`} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.05)",
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: m.role === "owner"
                ? "linear-gradient(135deg,#fde047,#f59e0b)"
                : "rgba(255,255,255,0.08)",
              color: m.role === "owner" ? "#1f1300" : "#fff",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontWeight: 900, fontSize: 13,
            }}>
              {(m.name?.[0] || m.email[0]).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>
                {m.name || m.email.split("@")[0]}
              </div>
              <div style={{ color: "#8b8ba7", fontSize: 12 }}>{m.email}</div>
            </div>
            {m.role === "owner" && (
              <span style={{
                padding: "3px 8px", background: "rgba(253,224,71,0.15)",
                color: "#fde047", borderRadius: 6, fontSize: 10,
                fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase",
              }}>{t.studio_team_owner_badge || "Owner"}</span>
            )}
          </div>
        ))}
      </div>

      {/* Pending invites */}
      {pending.length > 0 && (
        <div style={{
          background: "rgba(20,20,35,0.72)",
          border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden",
        }}>
          <div style={{
            padding: "12px 18px", borderBottom: "1px solid rgba(255,255,255,0.05)",
            color: "#8b8ba7", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}>
            {t.studio_team_pending_header || "Pending invites"} ({pending.length})
          </div>
          {pending.map((p) => (
            <div key={p.email} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
            }}>
              <Mail size={14} style={{ color: "#8b8ba7" }} />
              <div style={{ flex: 1, color: "#fff", fontSize: 14 }}>{p.email}</div>
              <div style={{ color: "#8b8ba7", fontSize: 11 }}>
                {t.studio_team_expires || "Expires"} {p.expires_at?.slice(0, 10)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
